import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { projectsService, usersService } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, Calendar, User, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

const COLORS = [
  "#6366F1", "#8B5CF6", "#EC4899", "#EF4444",
  "#F97316", "#EAB308", "#22C55E", "#14B8A6",
  "#06B6D4", "#3B82F6"
];

const STATUSES = ["Planning", "Active", "On Hold", "In Review", "Completed", "Archived"];
const PRIORITIES = ["High", "Medium", "Low"];

const NewProjectPanel = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchMembers, setSearchMembers] = useState("");
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      status: "Planning",
      priority: "Medium",
      color: COLORS[0],
      startDate: "",
      dueDate: "",
      assignedMembers: [],
      tags: []
    }
  });

  const selectedColor = watch("color");
  const selectedMembers = watch("assignedMembers");

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const users = await usersService.getAll();
        setTeamMembers(users);
      } catch (error) {
        console.error("Error fetching team:", error);
      }
    };
    fetchTeam();
  }, []);

  const handleMemberToggle = (userId) => {
    const current = selectedMembers || [];
    if (current.includes(userId)) {
      setValue("assignedMembers", current.filter(id => id !== userId));
    } else {
      setValue("assignedMembers", [...current, userId]);
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    member.name?.toLowerCase().includes(searchMembers.toLowerCase())
  );

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await projectsService.create({
        ...data,
        progress: 0,
        tasks: [],
        commentsCount: 0
      });
      toast({
        title: "Project created",
        description: "Project has been created successfully",
        variant: "success"
      });
      reset();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-screen w-full max-w-md bg-white z-50 shadow-2xl animate-slide-in overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-navy">New Project</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              {...register("name", { required: "Project name is required" })}
              placeholder="Enter project name"
              className={cn(errors.name && "border-red-500")}
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Enter project description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <select
                {...register("status")}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              >
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <div className="flex gap-2">
                {PRIORITIES.map(priority => (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => setValue("priority", priority)}
                    className={cn(
                      "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                      watch("priority") === priority
                        ? priority === "High" ? "bg-red-100 text-red-700 border-2 border-red-300"
                          : priority === "Medium" ? "bg-amber-100 text-amber-700 border-2 border-amber-300"
                          : "bg-green-100 text-green-700 border-2 border-green-300"
                        : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                    )}
                  >
                    {priority}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Color Tag</Label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setValue("color", color)}
                  className={cn(
                    "w-8 h-8 rounded-full transition-transform hover:scale-110",
                    selectedColor === color && "ring-2 ring-offset-2 ring-navy scale-110"
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <div className="relative">
                <Input
                  id="startDate"
                  type="date"
                  {...register("startDate")}
                  className="pl-10"
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <div className="relative">
                <Input
                  id="dueDate"
                  type="date"
                  {...register("dueDate")}
                  className="pl-10"
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign Team Members</Label>
            <div className="relative">
              <Input
                placeholder="Search team members..."
                value={searchMembers}
                onChange={(e) => setSearchMembers(e.target.value)}
                className="pl-10"
              />
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
              {filteredMembers.map(member => (
                <label
                  key={member.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers?.includes(member.id)}
                    onChange={() => handleMemberToggle(member.id)}
                    className="w-4 h-4 rounded border-slate-300 text-action-blue focus:ring-action-blue"
                  />
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {member.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <span className="text-sm text-navy">{member.name}</span>
                </label>
              ))}
              {filteredMembers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">No team members found</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <div className="relative">
              <Input
                id="tags"
                {...register("tags")}
                placeholder="e.g. marketing, Q1, urgent"
                className="pl-10"
              />
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-navy hover:bg-navy-light">
              {loading ? "Creating..." : "Create Project"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default NewProjectPanel;